import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X, Send, Cpu, User, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ChatbotService } from '../services/ChatbotService';

const readStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const buildInitialMessages = (isAuthenticated) => ([
  {
    id: 1,
    text: isAuthenticated
      ? "Hello! I'm LoadIQ's advanced Energy AI. I can help analyze grid patterns across Germany, UK, USA, and India. How can I assist you today?"
      : "Hello! I can answer general energy questions. Sign in to unlock your saved account history, dashboard context, and prediction results.",
    sender: 'ai'
  }
]);

const inlineMarkdownToNodes = (text) => {
  const segments = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*|https?:\/\/[^\s]+|\n)/g;
  let lastIndex = 0;

  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(text.slice(lastIndex, match.index));
    }
    segments.push(match[0]);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }

  return segments.filter(Boolean).map((segment, index) => {
    if (segment === '\n') {
      return <br key={`br-${index}`} />;
    }

    if (segment.startsWith('**') && segment.endsWith('**')) {
      return <strong key={`strong-${index}`}>{segment.slice(2, -2)}</strong>;
    }

    if (segment.startsWith('*') && segment.endsWith('*')) {
      return <em key={`em-${index}`}>{segment.slice(1, -1)}</em>;
    }

    if (segment.startsWith('`') && segment.endsWith('`')) {
      return (
        <code key={`code-${index}`} className="rounded bg-slate-900/80 px-1.5 py-0.5 font-mono text-[0.85em] text-indigo-200">
          {segment.slice(1, -1)}
        </code>
      );
    }

    if (/^https?:\/\/[^\s]+$/.test(segment)) {
      return (
        <a
          key={`link-${index}`}
          href={segment}
          target="_blank"
          rel="noreferrer"
          className="text-indigo-300 underline underline-offset-2 hover:text-indigo-200"
        >
          {segment}
        </a>
      );
    }

    return <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>;
  });
};

const parseMessageBlocks = (rawText) => {
  const normalizedText = String(rawText || '').replace(/\r\n/g, '\n').trim();
  if (!normalizedText) {
    return [];
  }

  const lines = normalizedText.split('\n');
  const blocks = [];
  let paragraphBuffer = [];
  let listBuffer = null;
  let codeBuffer = null;
  let quoteBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length) {
      blocks.push({ type: 'paragraph', text: paragraphBuffer.join(' ') });
      paragraphBuffer = [];
    }
  };

  const flushList = () => {
    if (listBuffer && listBuffer.items.length) {
      blocks.push({ type: 'list', ordered: listBuffer.ordered, items: listBuffer.items });
    }
    listBuffer = null;
  };

  const flushQuote = () => {
    if (quoteBuffer.length) {
      blocks.push({ type: 'quote', text: quoteBuffer.join(' ') });
      quoteBuffer = [];
    }
  };

  const flushCode = () => {
    if (codeBuffer) {
      blocks.push({ type: 'code', language: codeBuffer.language, text: codeBuffer.lines.join('\n') });
    }
    codeBuffer = null;
  };

  lines.forEach((line) => {
    const trimmed = line.trimEnd();
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    const unorderedMatch = trimmed.match(/^[-*•]\s+(.*)$/);
    const orderedMatch = trimmed.match(/^\d+[.)]\s+(.*)$/);
    const quoteMatch = trimmed.match(/^>\s?(.*)$/);
    const dividerMatch = trimmed.match(/^(\*{3,}|-{3,}|_{3,})$/);
    const codeFenceMatch = trimmed.match(/^```([\w-]*)\s*$/);

    if (codeFenceMatch) {
      if (codeBuffer) {
        flushCode();
      } else {
        flushParagraph();
        flushList();
        flushQuote();
        codeBuffer = { language: codeFenceMatch[1] || '', lines: [] };
      }
      return;
    }

    if (codeBuffer) {
      codeBuffer.lines.push(line);
      return;
    }

    if (!trimmed) {
      flushParagraph();
      flushList();
      flushQuote();
      return;
    }

    if (dividerMatch) {
      flushParagraph();
      flushList();
      flushQuote();
      return;
    }

    if (headingMatch) {
      flushParagraph();
      flushList();
      flushQuote();
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2].replace(/\s*\*{2,}\s*/g, ' ').trim()
      });
      return;
    }

    if (unorderedMatch || orderedMatch) {
      flushParagraph();
      flushQuote();
      if (!listBuffer) {
        listBuffer = {
          ordered: Boolean(orderedMatch),
          items: []
        };
      }
      listBuffer.items.push((unorderedMatch || orderedMatch)[1]);
      return;
    }

    if (quoteMatch) {
      flushParagraph();
      flushList();
      quoteBuffer.push(quoteMatch[1]);
      return;
    }

    flushList();
    flushQuote();
    paragraphBuffer.push(trimmed);
  });

  flushParagraph();
  flushList();
  flushQuote();
  flushCode();

  return blocks;
};

const MessageContent = ({ text }) => {
  const blocks = parseMessageBlocks(text);

  if (!blocks.length) {
    return null;
  }

  return (
    <div className="space-y-3 leading-relaxed">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const headingSizes = {
            1: 'text-base',
            2: 'text-[0.98rem]',
            3: 'text-[0.95rem]'
          };

          return (
            <div key={`heading-${index}`} className={`font-semibold text-slate-100 ${headingSizes[block.level] || 'text-[0.9rem]'}`}>
              {inlineMarkdownToNodes(block.text)}
            </div>
          );
        }

        if (block.type === 'list') {
          return (
            <div key={`list-${index}`} className="space-y-2">
              {block.items.map((item, itemIndex) => (
                <div key={`list-item-${index}-${itemIndex}`} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
                  <span>{inlineMarkdownToNodes(item)}</span>
                </div>
              ))}
            </div>
          );
        }

        if (block.type === 'quote') {
          return (
            <div key={`quote-${index}`} className="rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2 text-slate-300">
              <div className="border-l-2 border-indigo-500 pl-3">
                {inlineMarkdownToNodes(block.text)}
              </div>
            </div>
          );
        }

        if (block.type === 'code') {
          return (
            <pre key={`code-${index}`} className="overflow-x-auto rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-[0.8rem] text-slate-200">
              <code className="font-mono">{block.text}</code>
            </pre>
          );
        }

        return (
          <p key={`paragraph-${index}`} className="whitespace-pre-wrap text-slate-200">
            {inlineMarkdownToNodes(block.text)}
          </p>
        );
      })}
    </div>
  );
};

const EnergyChatbot = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => buildInitialMessages(false));
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const isAuthenticated = Boolean(user?.uid);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  useEffect(() => {
    setMessages(buildInitialMessages(isAuthenticated));
    setInput('');
    setIsLoading(false);
  }, [isAuthenticated]);

  const getAppContext = (userId) => {
    if (!userId) {
      return null;
    }

    const pendingInviteContext = readStoredJson('pendingInviteContext');
    const userProfile = readStoredJson(`userProfile_${userId}`);
    const userHistory = readStoredJson(`analyticsHistory_${userId}`) || [];
    const latestPrediction = readStoredJson(`loadiq_latest_prediction_context_${userId}`) || userHistory[0] || null;
    const dashboardOverview = readStoredJson(`loadiq_overview_context_${userId}`);
    const advancedAnalytics = readStoredJson(`loadiq_advanced_analytics_context_${userId}`);

    return {
      workspaceName: userProfile?.workspaceName || pendingInviteContext?.workspaceName || '',
      organization: userProfile?.organization || pendingInviteContext?.organization || '',
      latestPrediction,
      recentHistory: Array.isArray(userHistory) ? userHistory.slice(0, 5) : [],
      overview: dashboardOverview,
      advancedAnalytics
    };
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { id: Date.now(), text: input, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const context = isAuthenticated ? getAppContext(user.uid) : null;
      const country = context?.country || 'Germany';
      const reply = await ChatbotService.sendMessage(userMessage.text, country, context);
      setMessages(prev => [...prev, { id: Date.now() + 1, text: reply, sender: 'ai' }]);
    } catch (error) {
      setMessages(prev => [...prev, { id: Date.now() + 1, text: error.message || "Failed to connect to the AI service.", sender: 'ai' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 p-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full shadow-lg shadow-indigo-600/30 transition-all hover:scale-105 z-50 flex items-center justify-center"
      >
        <MessageSquare size={24} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed inset-x-3 bottom-20 top-20 h-auto max-h-[calc(100vh-5rem)] sm:inset-auto sm:bottom-24 sm:right-6 sm:left-auto sm:w-[400px] sm:h-[70vh] sm:max-h-[550px] bg-slate-900 border border-slate-700/50 rounded-2xl sm:rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-indigo-600 p-4 flex justify-between items-center text-white shadow-md pt-[max(1rem,env(safe-area-inset-top))]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Cpu size={18} />
                </div>
                <div>
                  <h3 className="font-bold text-sm">LoadIQ AI Assistant</h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isAuthenticated ? 'bg-green-400' : 'bg-amber-300'}`}></span>
                    <span className="text-[10px] text-indigo-100 tracking-wider">
                      {isAuthenticated ? 'ONLINE' : 'SIGN IN FOR HISTORY'}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
                <X size={20} />
              </button>
            </div>

            {/* Chat Area */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-4 bg-slate-950/50">
              {!isAuthenticated && (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-xs text-amber-100">
                  <div className="font-semibold">Sign in to unlock your saved history and prediction results.</div>
                  <div className="mt-1 text-amber-100/80">You can still chat normally, but no account data will be used until you log in.</div>
                  <button
                    onClick={() => navigate('/login')}
                    className="mt-3 inline-flex items-center justify-center rounded-xl bg-amber-400 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-950 transition-colors hover:bg-amber-300 active:scale-95"
                  >
                    Sign in
                  </button>
                </div>
              )}
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[92%] sm:max-w-[85%] rounded-2xl p-3 text-sm shadow-md ${msg.sender === 'user' ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-slate-800 text-slate-200 border border-slate-700 rounded-bl-sm'}`}>
                    <div className="flex items-center gap-2 mb-1 opacity-70">
                      {msg.sender === 'user' ? <User size={12} /> : <Cpu size={12} />}
                      <span className="text-[10px] uppercase font-bold">{msg.sender === 'user' ? 'You' : 'LoadIQ AI'}</span>
                    </div>
                    <MessageContent text={msg.text} />
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-800 text-slate-200 border border-slate-700 rounded-2xl rounded-bl-sm p-3 text-sm shadow-md flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-indigo-400" />
                    <span className="text-xs text-slate-400">Analyzing...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-slate-900 border-t border-slate-700/50">
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Ask about grids, patterns..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 pl-4 pr-12 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className="absolute right-2 p-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white rounded-lg transition-colors flex items-center justify-center active:scale-95"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EnergyChatbot;
